import { useEffect, useRef, type ReactNode } from 'react';
import { Icon } from './Icon';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  width?: number; // optional max width override (px)
}

export function Modal({ open, onClose, title, children, footer, width }: ModalProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  // Keep the latest onClose in a ref so the open-effect doesn't depend on it.
  // (Pages pass an inline onClose; if it were in the deps the effect — incl. the
  // auto-focus below — would re-run on EVERY render, i.e. every keystroke, and
  // steal focus back to the first field. The effect must run only on open.)
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // ESC to close + auto-focus first field — ONLY when the modal opens.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', onKey);
    const first = cardRef.current?.querySelector<HTMLElement>(
      'input, textarea, select, button',
    );
    first?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="modal-backdrop"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div
        ref={cardRef}
        className="modal-card"
        style={width ? { maxWidth: `${width}px` } : undefined}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="modal-header">
          <h2 id="modal-title" className="modal-title">
            {title}
          </h2>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            aria-label="Close dialog"
          >
            <Icon name="x" size={16} />
          </button>
        </header>
        <div className="modal-body">{children}</div>
        {footer && <footer className="modal-footer">{footer}</footer>}
      </div>
    </div>
  );
}
